<template>
  <div>
    <div class="searchForm">
      <div class="is-flex is-align-items-center is-justify-content-space-between mb-4">
        <div v-if="showDateInput" class="mr-4" style="max-width: 150px;">
          <div class="has-text-weight-bold mb-1">{{ $t('Components.Availability.CheckAvailability.Header_Date') }}</div>

          <v-date-picker
            mode="single"
            v-model="dateOne"
            :value="formatDates(dateOne)"
          >
            <p class="control has-icons-left">
              <input
                type="text"
                class="input"
                :id="'datepicker-trigger' + fieldId"
                readonly
                :value="formatDates(dateOne)"
              />
              <span class="icon is-left">
                <font-awesome-icon :icon="['fas', 'calendar-alt']" />
              </span>
            </p>
          </v-date-picker>
        </div>

        <div v-if="meetingtypeId < 3" class="mr-4">
          <div class="has-text-weight-bold mb-1">{{ $t('Components.Availability.CheckAvailability.Header_Time') }}</div>

          <div class="is-flex is-align-items-center is-justify-content-space-between">
            <div class="control has-icons-left">
              <div
                class="select is-fullwidth"
                :class="{ 'is-danger': endTimeErrors.length }"
              >
                <select v-model="mStartTime">
                  <option
                    v-for="timeSlot in timeSlots"
                    :key="timeSlot"
                    :value="timeSlot"
                    :selected="mStartTime === timeSlot"
                  >
                    {{ minutesToTime(timeSlot) }}
                  </option>
                </select>
              </div>
              <div
                v-if="endTimeErrors.length"
                class="has-text-danger is-size-7"
                v-html="endTimeErrors"
              ></div>
              <div class="icon is-left">
                <font-awesome-icon :icon="['fas', 'clock']" />
              </div>
            </div>

            <div class="mx-1">—</div>

            <div class="control has-icons-left">
              <div
                class="select is-fullwidth"
                :class="{ 'is-danger': endTimeErrors.lenth }"
              >
                <select
                  v-model="mEndTime"
                  :error="endTimeErrors"
                  @change="$v.mEndTime.$touch()"
                >
                  <option
                    v-for="timeSlot in timeSlots"
                    :key="timeSlot"
                    :value="timeSlot"
                    :selected="mEndTime === timeSlot"
                  >
                    {{ minutesToTime(timeSlot) }}
                  </option>
                </select>
              </div>
              <div class="icon is-left">
                <font-awesome-icon :icon="['fas', 'clock']" />
              </div>
            </div>
          </div>
        </div>
        

        <div v-if="meetingtypeId !== 2" class="mr-4" style="max-width: 150px;">
          <div class="has-text-weight-bold mb-1">{{ $t('Components.Availability.CheckAvailability.Header_Seats') }}</div>

          <p class="control has-icons-left">
            <input
              type="text"
              v-model.number="mSeats"
              @keyup="getLocationSpaces"
              @keyup.13="getAvailability"
              class="input"
              :class="{ 'is-danger': seatsErrors.length }"
              @input="
                () => {
                  $v.mSeats.$touch()
                }
              "
              @blur="$v.mSeats.$touch()"
            />
            <span class="icon is-left">
              <font-awesome-icon :icon="['fas', 'user-check']" />
            </span>
          </p>
          <div
            v-if="seatsErrors.length"
            class="has-text-danger is-size-7"
            v-html="seatsErrors"
          ></div>
        </div>

        <div class="mr-4">
          <div class="has-text-weight-bold mb-1">{{ $t('Components.Availability.CheckAvailability.Header_Space') }}</div>

          <div class="control has-icons-left">
            <div
              class="select is-fullwidth"
              :class="{ 'is-loading': isLoadingSpaces }"
            >
              <select v-model="mSpaceId" style="max-width: 200px;">
                <option :value="0" :select="mSpaceId === 0">
                  {{
                    $t(
                      'Components.Availability.CheckAvailability.Select_OptionAllSpaces'
                    )
                  }}
                </option>
                <option
                  v-for="space in filteredSpaces"
                  :key="space.Id"
                  :value="space.Id"
                  :select="mSpaceId === space.Id"
                >
                  {{ space.Name }}
                </option>
              </select>
            </div>
            <div class="icon is-small is-left">
              <font-awesome-icon :icon="['fas', 'chair']" />
            </div>
          </div>
        </div>

        <div v-if="meetingtypeId === 1">
          <div class="has-text-weight-bold mb-1">{{ $t('Components.Availability.CheckAvailability.Header_Configuration') }}</div>

          <div class="control has-icons-left">
            <div class="select is-fullwidth">
              <select v-model="mSettingId">
                <option :value="0" :selected="mSettingId === 0">
                  {{
                    $t('Components.Availability.CheckAvailability.Select_OptionAllConfigurations')
                  }}
                </option>
                <option
                  v-for="setting in filteredSettings"
                  :key="setting.SettingId"
                  :value="setting.SettingId"
                  :selected="setting.SettingId === mSettingId"
                >
                  {{ setting.SettingId | getSettingName }} [{{
                    setting.MinSeats
                  }}
                  - {{ setting.MaxSeats }}]
                </option>
              </select>
            </div>
            <div v-if="meetingtypeId === 1" class="icon is-left">
              <font-awesome-icon :icon="['fas', 'sliders-h']" />
            </div>
          </div>
        </div>
      </div>

      <div class="has-text-right">
        <button
          @click="getAvailability"
          class="button"
          :class="{
            'is-loading': isSearching || isDisabled,
            'is-primary': !blockSearchButton,
            'is-disabled': blockSearchButton,
          }"
          :disabled="blockSearchButton"
        >
          {{ $t('Components.Availability.CheckAvailability.Button_Search') }}
        </button>
      </div>
    </div>

    <hr class="my-6" />

    <ui-loader v-if="isSearching" />

    <template v-if="!isSearching && filteredAvailability.Locations">
      <nav v-if="selectedSpaces.length" class="navbar is-hidden-print">
        <a @click="availability = {}" class="navbar-item">
          <span class="icon is-small">
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
          </span>
          <span>{{
            $t('Components.Reservation.ReservationMailLog.Icon_Back')
          }}</span>
        </a>
      </nav>

      <AvailableLocation
        v-for="location in filteredAvailability.Locations"
        :key="location.Id"
        :isEmployee="isEmployee"
        :availableLocation="location"
        :meetingtypeId="meetingtypeId"
        :selectedSpaceId="mSpaceId"
      />
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { EventBus } from '@/eventbus/event-bus'
import { mapState, mapActions } from 'vuex'
import availabilityProvider from '@/providers/availability'
import spaceProvider from '@/providers/space'
import { minValue, required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'

import VCalendar from 'v-calendar'
Vue.use(VCalendar, {})

const touchMap = new WeakMap()

export default {
  name: 'CheckAvailability',

  components: {
    AvailableLocation: () => import('@/components/Availability/AvailableLocation'),
  },

  props: {
    isEmployee: {
      type: Boolean,
      default: false,
    },
    autoSearch: {
      default: false,
      type: Boolean,
    },
    canChangeDate: {
      default: false,
      type: Boolean,
    },
    channelId: {
      default: 0,
      type: Number,
    },
    countryId: {
      default: 0,
      type: Number,
    },
    endDate: {
      default: function () {
        return new Date()
      },
      type: Date,
    },
    endTime: {
      default: 1020,
      type: Number,
    },
    locationId: {
      default: 0,
      type: Number,
    },
    meetingtypeId: {
      default: 1,
      type: Number,
    },
    seats: {
      default: 1,
      type: Number,
    },
    settingId: {
      default: 0,
      type: Number,
    },
    showDateInput: {
      default: false,
      type: Boolean,
    },
    startDate: {
      default: function () {
        return new Date()
      },
      type: Date,
    },
    startTime: {
      default: 540,
      type: Number,
    },
    tenderId: {
      default: 0,
      type: Number,
    },
    voucherId: {
      default: 0,
      type: Number,
    },
    selectedSpaces: {
      default: function () {
        return []
      },
      type: Array,
    },

    isDisabled: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      availability: {},
      availabilityChecked: false,
      closeTime: 0,
      dateOne: this.startDate,
      dateTwo: this.endDate,
      fieldId: new Date().getTime(),
      isLoadingSettings: false,
      isLoadingSpaces: false,
      isSearching: false,
      mEndTime: this.endTime,
      mSeats: this.meetingtypeId === 2 ? 1 : this.seats,
      mSettingId: this.settingId,
      mSpaceId: 0,
      mStartTime: this.startTime,
      openTime: 0,
      searchTerm: '',
      settings: [],
      spaces: [],
      timeSlots: []
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      mEndTime: {
        minValue: (value) => value > this.mStartTime,
      },

      mSeats: {
        required,
        minValue: minValue(1),
      },
    }
    return output
  },

  computed: {
    ...mapState('locationStore', ['locationState']),
    ...mapState('spaceStore', ['spacesPageState']),

    channels() {
      let channels = []

      if (this.locationState) {
        channels = this.locationState.Channels
      }

      return channels
    },

    filteredAvailability() {
      let availability = this.availability

      if (
        this.availability &&
        this.availability.Locations &&
        this.availability.Locations.length > 0 &&
        this.selectedSpaces &&
        this.selectedSpaces.length > 0
      ) {
        let location = this.availability.Locations.find(
          (l) => l.LocationId == this.locationId
        )

        let spaces = []
        spaces = location.Spaces.filter((s) => {
          return !this.selectedSpaces.find((sp) => {
            return (
              sp.SpaceId === s.SpaceId &&
              ((sp.StartMinutes >= s.StartMinutes &&
                sp.StartMinutes <= s.EndMinutes) ||
                (sp.EndMinutes >= s.StartMinutes &&
                  sp.EndMinutes <= s.EndMinutes))
            )
          })
        })

        let locationIndex = this.availability.Locations.findIndex(
          (l) => l.LocationId == this.locationId
        )
        availability.Locations[locationIndex].Spaces = spaces
        this.$emit('callbackShowSelectedSpaces', false)
      }

      return availability
    },

    filteredSpaces() {
      let spaces = this.spacesPageState.spaces

      if (this.meetingtypeId > 0) {
        spaces = spaces.filter((s) =>
          s.Meetingtypes.some((m) => m.MeetingtypeId === this.meetingtypeId)
        )
      }

      if (this.mSettingId > 0) {
        spaces = spaces.filter((s) =>
          s.Settings.some((setting) => setting.SettingId === this.mSettingId)
        )
      }

      return spaces
    },

    filteredSettings() {
      let settings = [] //this.settings
      let spaces = this.spacesPageState.spaces

      if (this.mSpaceId > 0) {
        spaces = this.spacesPageState.spaces.filter(
          (s) => s.Id === this.mSpaceId
        )
      }

      if (spaces) {
        for (let i = 0; i < spaces.length; i++) {
          let space = spaces[i]

          for (let j = 0; j < space.Settings.length; j++) {
            let setting = space.Settings[j]

            let settingIndex = settings.findIndex(
              (s) => s.SettingId === setting.SettingId
            )
            if (settingIndex === -1) {
              // Add setting
              settings.push({
                SettingId: setting.SettingId,
                MinSeats: setting.MinSeats,
                MaxSeats: setting.MaxSeats,
              })
            } else {
              // Update min / max
              settings[settingIndex].MinSeats =
                setting.MinSeats < settings[settingIndex].MinSeats
                  ? setting.MinSeats
                  : settings[settingIndex].MinSeats
              settings[settingIndex].MaxSeats =
                setting.MaxSeats > settings[settingIndex].MaxSeats
                  ? setting.MaxSeats
                  : settings[settingIndex].MaxSeats
            }
          }
        }
      }

      return settings
    },

    blockSearchButton() {
      let self = this
      return self.seatsErrors.length > 0 || self.endTimeErrors.length > 0
    },

    endTimeErrors() {
      let errors = ''
      if (!this.$v.mEndTime.$dirty) return errors
      if (!this.$v.mEndTime.minValue) {
        errors = this.$t(
          'Components.Availability.CheckAvailability.Message_TimeError'
        )
      }
      return errors
    },

    seatsErrors() {
      let errors = ''
      if (!this.$v.mSeats.$dirty) return errors
      if (!this.$v.mSeats.required) {
        errors = this.$t(
          'Components.Availability.CheckAvailability.Message_Required'
        )
      }
      if (!this.$v.mSeats.minValue) {
        errors = this.$t(
          'Components.Availability.CheckAvailability.Message_SeatsError'
        )
      }
      return errors
    },
  },

  created() {
    this.fillTimeSlots()

    if (this.spacesPageState.spaces.length === 0) {
      this.getSpaces(this.locationId)
    }

    EventBus.$on('spaceSelected', this.setAvailability)
  },

  mounted() {
    if (this.autoSearch) {
      this.getAvailability()
    }
  },

  beforeDestroy() {
    this.availability = {}

    //EventBus.$off('spaceSelected')
  },

  methods: {
    ...mapActions('spaceStore', ['getSpaces']),

    getLocationSpaces() {
      this.getSpaces(this.locationId).then(() => {
        this.isLoadingSpaces = false
      })
    },

    setAvailability() {
      this.availability = {}
    },

    formatDates(dateOne = '', dateTwo = '') {
      let formattedDates = ''

      if (dateOne) {
        formattedDates = this.$options.filters.dateFormat(
          dateOne,
          this.$i18n.locale,
          'longDateFormat'
        )
      }
      return formattedDates
    },

    fillTimeSlots() {
      let max = 1440
      let minutes = 30

      let i = 0
      while (i <= max) {
        this.timeSlots.push(i)
        i = i + minutes
      }
    },

    minutesToTime(minutes) {
      let hour = Number(Math.floor(minutes / 60))
      let minute = Number(minutes - hour * 60)

      return (
        (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute
      )
    },

    getAvailability() {
      let self = this
      self.availability = {}
      this.$v.$touch()
      if (this.$v.$invalid) {
        // 'ERROR'
        self.isSearching = false
        this.$emit('callbackShowSelectedSpaces', true)
      } else {
        self.isSearching = true
        this.$emit('callbackShowSelectedSpaces', false)

        let startDate = format(self.dateOne, 'yyyy-MM-dd');
        let endDate = format(self.dateTwo, 'yyyy-MM-dd');

        availabilityProvider.methods
          .getAvailability(
            startDate,
            self.meetingtypeId < 3 ? startDate : endDate,
            self.channelId,
            self.countryId,
            self.locationId,
            self.searchTerm,
            self.meetingtypeId,
            self.meetingtypeId === 2 ? 1 : self.mSeats,
            self.mStartTime,
            self.mEndTime,
            self.meetingtypeId === 2 ? 0 : self.mSettingId,
            self.mSpaceId,
            self.voucherId,
            self.tenderId,
            -100,
            this.$i18n.locale,
            this.isEmployee
          )
          .then((response) => {
            self.availability = response.data

            if (self.availability && self.availability.Locations.length > 0) {
              let searchId = self.availability.Locations[0].SearchId
              self.$emit('searchInitiated', searchId)
            }
          })
          .finally(() => {
            self.isSearching = false
            // this.$emit('callbackShowSelectedSpaces', false)
            self.availabilityChecked = true
          })
      }
    },
  },
}
</script>
